import { Button } from "@/components/ui/button";
import { forgeStepState } from "@/lib/jira";
import type { JiraConnection } from "@/types/api";
import { useEffect, useRef, useState } from "react";

interface JiraForgeStepProps {
	status: JiraConnection;
	/** Organization owners and admins get the install link; everyone else just sees where things stand. */
	canEdit: boolean;
	onCheck: () => void;
	checking: boolean;
}

/** Step 2 of connecting Jira: the Curo app that posts the comments, installed once by a Jira site admin. */
export function JiraForgeStep({
	status,
	canEdit,
	onCheck,
	checking,
}: JiraForgeStepProps) {
	const state = forgeStepState(status);
	if (state === "not_applicable" || !status.connected) return null;

	if (state === "ready") {
		return (
			<div className="rounded-md border border-success-stroke bg-success-surface p-3">
				<p className="text-caption font-medium text-success">
					✓ Step 2 — Curo app installed
				</p>
				<p className="text-caption text-muted-foreground">
					Comments on your issues are posted by the Curo app.
				</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3 rounded-md border border-warning bg-warning-surface p-3">
			<div>
				<p className="text-caption font-medium text-heading">
					Step 2 — Install the Curo app
				</p>
				<p className="text-caption text-muted-foreground">
					Curo posts its research as the Curo app, which a Jira site admin
					approves once for {status.siteUrl}. Until then research is prepared
					but comments are held; they post automatically after the installation.
				</p>
			</div>
			{state === "install" && canEdit && status.forgeInstallUrl ? (
				<InstallActions
					installUrl={status.forgeInstallUrl}
					onCheck={onCheck}
					checking={checking}
				/>
			) : null}
			{state === "install" && !canEdit ? (
				<p className="text-caption text-muted-foreground">
					Ask an organization admin to open the installation page, or a Jira
					site admin to approve the Curo app.
				</p>
			) : null}
			{state === "install_unlinked" ? (
				<p className="text-caption text-muted-foreground">
					The installation link is not available yet. Ask Curo for the
					installation link for the Curo app.
				</p>
			) : null}
			{state === "install" ? (
				<p className="text-caption text-muted-foreground">
					Waiting for the installation… this page updates automatically.
				</p>
			) : null}
		</div>
	);
}

function InstallActions({
	installUrl,
	onCheck,
	checking,
}: {
	installUrl: string;
	onCheck: () => void;
	checking: boolean;
}) {
	const [copied, setCopied] = useState(false);
	const resetCopied = useRef<number | undefined>(undefined);
	useEffect(() => () => window.clearTimeout(resetCopied.current), []);

	async function copyLink() {
		try {
			await navigator.clipboard.writeText(installUrl);
			setCopied(true);
			window.clearTimeout(resetCopied.current);
			resetCopied.current = window.setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard access can be denied; the install page can still be opened directly.
		}
	}

	return (
		<div className="flex flex-col gap-2">
			<p className="text-caption text-muted-foreground">
				You need to be a Jira site admin to approve it. If you are not, copy the
				link and send it to one.
			</p>
			<div className="flex flex-wrap gap-2">
				<Button asChild>
					<a href={installUrl} target="_blank" rel="noopener noreferrer">
						Open installation page
					</a>
				</Button>
				<Button variant="outline" onClick={copyLink}>
					{copied ? "Copied" : "Copy link"}
				</Button>
				<Button variant="outline" onClick={onCheck} disabled={checking}>
					{checking ? "Checking…" : "Check now"}
				</Button>
			</div>
		</div>
	);
}
